import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Zap,
  Droplets,
  Brain,
  Database,
  ArrowRight,
  Building2,
  Factory,
  Landmark,
  Server,
  Globe,
  TrendingUp,
  Clock,
  Target,
  Layers3,
  Flame,
  Thermometer,
  Wind,
  CircuitBoard,
  Atom,
  Truck,
  Search,
  DollarSign,
  CheckCircle2,
  Briefcase,
  MapPin,
  AlertTriangle,
  ChevronRight,
  BarChart3,
} from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";

// ─────────────────────────────────────────────────────────────
// DATA
// ─────────────────────────────────────────────────────────────

const stages = [
  {
    id: "ingest",
    num: "01",
    label: "Wide-Aperture Ingest",
    sub: "Every parcel. Three hard gates. Nothing else filtered.",
    Icon: Search,
    color: "from-sky-500 to-cyan-400",
    lightBg: "bg-sky-50",
    lightBorder: "border-sky-200",
    dotColor: "bg-sky-500",
    summary:
      "Traditional site selection starts with 10,000 parcels and filters down to 20. That's a losing game — everyone fishes in the same pond. This system inverts the model: ingest everything above 40 acres in target geographies, apply only three binary hard gates, and pass everything else to the solution generators. The addressable universe goes from hundreds to tens of thousands overnight.",
    bullets: [
      "County assessor + GIS data for every parcel in target states",
      "Only three filters: land exists, jurisdiction non-hostile, geographic viability",
      "No power filter — that's an engineering problem, not a gate",
      "No water filter — cooling design eliminates the dependency",
      "Ownership resolution through LLC/trust chains",
      "Continuous scan — new listings surface within 24 hours",
    ],
  },
  {
    id: "power",
    num: "02",
    label: "Power Agent",
    sub: "Don't find sites with power. Find power for sites.",
    Icon: Zap,
    color: "from-amber-500 to-orange-400",
    lightBg: "bg-amber-50",
    lightBorder: "border-amber-200",
    dotColor: "bg-amber-500",
    summary:
      "Power is the binding constraint on 70%+ of sites. Everyone else rejects parcels without utility allocation. This agent runs six parallel pathways — grid interconnection, stranded asset capture, behind-the-meter generation, PPA origination, SMR evaluation, and rapid deployment — and produces a fully-costed, phased power strategy for every candidate parcel in under 60 seconds.",
    bullets: [
      "Six parallel power pathways evaluated simultaneously",
      "Stranded asset detection: retiring coal plants, closed factories, BRAC bases",
      "Behind-the-meter gas, solar+BESS, hybrid microgrid modeling",
      "PPA matching against every project in ISO interconnection queues",
      "Live equipment inventory: generators, transformers, switchgear",
      "Revenue acceleration math: speed-to-MW is the ranking metric",
      "Phased deployment: rapid bridge → permanent → SMR long-term",
    ],
  },
  {
    id: "water",
    num: "03",
    label: "Water Agent",
    sub: "You're not buying water. You're buying thermal rejection.",
    Icon: Droplets,
    color: "from-blue-500 to-cyan-500",
    lightBg: "bg-blue-50",
    lightBorder: "border-blue-200",
    dotColor: "bg-blue-500",
    summary:
      "Water scarcity kills deals in Phoenix, West Texas, and Nevada. But the real need isn't water — it's cooling. Seven pathways from municipal supply through liquid cooling to waste-heat-powered desalination. The agent converts 'water-constrained' into 'water-irrelevant' and turns waste heat into community goodwill that accelerates every permit in the pipeline.",
    bullets: [
      "Seven cooling pathways: municipal, groundwater, reclaimed, air-cooled, liquid cooling, alternative sources, heat monetization",
      "Climate-adjusted 20-year supply risk modeling",
      "Liquid cooling analysis: 1.10 PUE, 80% smaller footprint, near-zero water",
      "Reclaimed water deal structures that solve the utility's problem",
      "Waste heat monetization: schools, hospitals, greenhouses, aquaculture",
      "Cross-agent referral: cooling choice changes power requirement",
      "Community benefit packages auto-generated for rezoning hearings",
    ],
  },
  {
    id: "economics",
    num: "04",
    label: "Deal Economics Engine",
    sub: "Every 'imperfect' site vs. every 'obvious' alternative.",
    Icon: TrendingUp,
    color: "from-emerald-500 to-teal-400",
    lightBg: "bg-emerald-50",
    lightBorder: "border-emerald-200",
    dotColor: "bg-emerald-500",
    summary:
      "The Power Agent and Water Agent produce solutions. The Deal Economics Engine rolls them into a single number: total cost to operational MW, compared against the market alternative. An 'imperfect' site in West Texas with behind-the-meter gas and liquid cooling often beats a 'perfect' site in Loudoun County by $200M and 14 months.",
    bullets: [
      "Land + power solution + cooling solution + construction = total deal cost",
      "Revenue acceleration value: months saved × revenue per MW per month",
      "Market comparison: your site vs. the 'obvious' alternative in the same region",
      "Portfolio construction: buy 10, develop 6, hold 4 as optionality",
      "Convergence loop: Power ↔ Water iterate until optimized",
      "Every outcome feeds Brain — the system learns from every deal",
    ],
  },
  {
    id: "pipeline",
    num: "05",
    label: "Pipeline + Alerts",
    sub: "The machine that never sleeps.",
    Icon: Target,
    color: "from-violet-500 to-fuchsia-500",
    lightBg: "bg-violet-50",
    lightBorder: "border-violet-200",
    dotColor: "bg-violet-500",
    summary:
      "This isn't a tool you query. It's a machine that runs 24/7. Daily scans detect retiring power plants, freed interconnection capacity, available generators, drought escalations, competitor filings, and incentive changes. Monday morning briefings rank the top 10 new opportunities by speed-to-MW.",
    bullets: [
      "Daily: stranded asset retirements, queue withdrawals, equipment availability",
      "Daily: drought monitor, aquifer levels, municipal water alerts",
      "Weekly: full parcel universe refresh, new listings, ownership changes",
      "Weekly: Power Opportunity Briefing — top 10 ranked by speed-to-MW",
      "Monthly: utility IRP scan, rate case changes, planned substations",
      "Competitor activity: site plans, interconnection filings, permit applications",
      "Moratorium risk: legislative agenda monitoring, accelerate-or-abort alerts",
    ],
  },
];

const rescueSteps = [
  {
    id: 0,
    label: "Parcel Discovered",
    agent: null,
    description: "AZ-Maricopa-4471 enters the system. 847 acres. Maricopa County, AZ. Asking: $4.2M.",
    badges: [
      { label: "No Grid Power", status: "red" },
      { label: "Water-Constrained", status: "red" },
      { label: "Zoned Agricultural", status: "red" },
    ],
    detail: null,
  },
  {
    id: 1,
    label: "Power Agent",
    agent: "⚡ Power Agent running...",
    description: "Six pathways evaluated in parallel. Solution identified.",
    badges: [
      { label: "Power Solved", status: "green" },
      { label: "Water-Constrained", status: "red" },
      { label: "Zoned Agricultural", status: "red" },
    ],
    detail: "Behind-the-meter gas via El Paso Natural Gas pipeline, 4.2 mi. 200MW in 16 months. Rapid deploy bridge available: 4× LM6000 turbines from cancelled LNG project. First MW in 10 months.",
  },
  {
    id: 2,
    label: "Water Agent",
    agent: "💧 Water Agent running...",
    description: "Seven cooling pathways evaluated. Water dependency eliminated.",
    badges: [
      { label: "Power Solved", status: "green" },
      { label: "Water Eliminated", status: "green" },
      { label: "Zoned Agricultural", status: "red" },
    ],
    detail: "Liquid cooling selected. PUE 1.10. Zero municipal water required. Dry cooler heat rejection — no evaporation, no water rights, no drought risk. Saves 40MW vs. evaporative design.",
  },
  {
    id: 3,
    label: "Entitlement Agent",
    agent: "📋 Entitlement Agent running...",
    description: "Rezoning pathway modeled. Community benefit package generated.",
    badges: [
      { label: "Power Solved", status: "green" },
      { label: "Water Eliminated", status: "green" },
      { label: "Entitlement Clear", status: "green" },
    ],
    detail: "Waste heat to 3 Maricopa school districts + regional hospital. Community benefit: $6.2M/year. 45 permanent jobs. $4.1M annual property tax. Rezoning approval probability: 92%.",
  },
  {
    id: 4,
    label: "Deal Economics",
    agent: "📊 Deal Economics Engine assembling...",
    description: "All solutions converged. Total deal costed. Market comparison complete.",
    badges: [
      { label: "Power Solved", status: "green" },
      { label: "Water Eliminated", status: "green" },
      { label: "Entitlement Clear", status: "green" },
    ],
    detail: null,
    result: {
      speed: "10 months to first MW",
      cost: "$310M cheaper than Loudoun County",
      acceleration: "+$538M revenue captured",
    },
  },
];

const corePrinciples = [
  { Icon: TrendingUp, color: "from-amber-500 to-orange-500", title: "Inversion, not filtration", body: "Everyone else filters 10,000 sites to 20. This system takes sites that are 60% qualified and manufactures the missing 40%. The addressable universe grows 50×." },
  { Icon: Clock, color: "from-emerald-500 to-sky-500", title: "Speed is the product", body: "Ranked by months-to-first-MW, not site quality score. A B+ site you energize in 14 months beats an A+ site that's 30 months out. Every month of acceleration is $40-80M in captured revenue." },
  { Icon: Brain, color: "from-violet-500 to-fuchsia-500", title: "Solution generation, not sifting", body: "Five parallel agents — Power, Water, Fiber, Hardening, Entitlement — take any parcel and output a costed, timed remediation plan. The moat is the solution layer, not the data." },
  { Icon: Database, color: "from-sky-500 to-indigo-500", title: "Compounding memory", body: "Every deal outcome — won, lost, passed — feeds back into Brain. After 20 deals, the system knows which utilities move fast, which counties are friendly, which brokers deliver." },
  { Icon: DollarSign, color: "from-red-500 to-amber-500", title: "Underdamp and eat false positives", body: "Land is less than 1% of the deal. Buy 10, use 4. Losing 4% across 6 winners is pennies. The cost of waiting is measured in billions of lost deal flow." },
  { Icon: Layers3, color: "from-cyan-500 to-emerald-500", title: "Cross-agent convergence", body: "Water choice changes power requirement. Power source enables cooling innovation. Heat monetization accelerates permits. Agents iterate until the total deal cost stabilizes." },
];

const opportunityCards = [
  {
    dot: "bg-amber-500",
    label: "STRANDED_ASSET_DETECTED",
    title: "The Retiring Coal Plant",
    narrative: "A 400MW coal plant in Ohio announces retirement in 2027. Its 345kV interconnection took 8 years and $120M to build. The utility hasn't decided what to do with the site yet. The system detects the EIA filing within 48 hours and cross-references 6 parcels within 5 miles with available acreage. Your competitor finds out when the Wall Street Journal writes about it six months later.",
    timeAdvantage: "6 months ahead",
    opportunityValue: "$120M+ interconnection asset",
  },
  {
    dot: "bg-emerald-500",
    label: "QUEUE_WITHDRAWAL",
    title: "The Queue Withdrawal",
    narrative: "A 200MW solar developer pulls out of the PJM interconnection queue. That frees 200MW of transmission capacity at a substation 8 miles from your active parcel. The queue data updates Tuesday morning. The system generates an alert by Tuesday 3 AM. You file your interconnection application Wednesday. Everyone else files in Q3 — after the next quarterly queue report.",
    timeAdvantage: "3–4 months ahead",
    opportunityValue: "200MW of freed capacity",
  },
  {
    dot: "bg-blue-500",
    label: "INVISIBLE_OPPORTUNITY",
    title: "The Produced Water Play",
    narrative: "A Permian Basin site with cheap land and abundant gas pipeline access but zero freshwater within 30 miles. Every competitor skips it. The Water Agent identifies 14 million barrels per day of produced water from nearby oil operations that operators currently pay $3/barrel to dispose of. You take their waste product, treat it, and cool your facility for less than municipal water rates. They pay you to solve their problem.",
    timeAdvantage: "No competition",
    opportunityValue: "Water at negative cost",
  },
];

const powerPathways = [
  { Icon: CircuitBoard, label: "Grid Interconnection", detail: "Nearest substation, queue depth, utility speed score, interconnection cost regression from FERC filings." },
  { Icon: Factory, label: "Stranded Asset Capture", detail: "Retiring coal plants, closing factories, BRAC bases. Their interconnections are already built and paid for." },
  { Icon: Flame, label: "Behind-the-Meter Generation", detail: "Gas pipeline proximity, solar+BESS sizing, hybrid microgrid economics, simplified pro forma per parcel." },
  { Icon: Wind, label: "PPA Origination", detail: "Every renewable project in ISO queues — permitted but unfunded. You bring bankable offtake, they bring power." },
  { Icon: Atom, label: "Small Modular Reactor", detail: "NRC-licensed designs, siting requirements, phased deployment: gas bridge today, SMR in 2032." },
  { Icon: Truck, label: "Rapid Deployment", detail: "Live equipment inventory: available generators, mobile substations, cancelled-project turbines. First MW in 10 months." },
];

const waterPathways = [
  { Icon: Droplets, label: "Municipal Supply", detail: "Utility identification, allocation headroom, rate structure, 20-year climate-adjusted supply risk, political risk scoring." },
  { Icon: Layers3, label: "Groundwater", detail: "Aquifer ID, well yield estimates, water quality, regulatory regime, sustainable yield assessment." },
  { Icon: Building2, label: "Reclaimed Water", detail: "Nearest WWTP, available effluent, pipeline cost, deal structures where you solve their infrastructure funding problem." },
  { Icon: Wind, label: "Air-Cooled (Zero Water)", detail: "PUE penalty modeling, capex premium, energy cost, payback vs. water right acquisition. Eliminates the constraint entirely." },
  { Icon: CircuitBoard, label: "Liquid Cooling", detail: "1.05-1.15 PUE, 80% smaller building, near-zero water. Higher density means smaller parcels qualify. The future, built today." },
  { Icon: Globe, label: "Alternative Sources", detail: "Brackish desal, produced water from O&G, rainwater harvesting, agricultural water rights, waste-heat-powered desalination." },
  { Icon: Thermometer, label: "Heat Monetization", detail: "Turn waste heat into revenue and community goodwill. Schools, hospitals, greenhouses. The play that wins rezoning votes." },
];

const scenarios = [
  {
    id: "houston",
    label: "Houston",
    sub: "Hurricane zone",
    rejection: "Rejected — hurricane zone. Risk score exceeds threshold. Removed from consideration.",
    solution: "Category 4 hardening: $15M capex. ERCOT power at some of the cheapest industrial rates in the US. No state income tax. Deep Gulf Coast labor pool. Savings vs. competing for scarce Virginia inventory: $50M+ over 10 years. Net advantage: +$35M and a site nobody else is bidding on.",
    metrics: [
      { label: "Hardening cost", value: "$15M" },
      { label: "vs. Virginia inventory", value: "–$50M" },
      { label: "Competition", value: "None" },
    ],
  },
  {
    id: "phoenix",
    label: "Phoenix",
    sub: "Water-scarce",
    rejection: "Rejected — water-constrained. Municipal allocation insufficient. Colorado River shortage risk flagged.",
    solution: "Liquid cooling eliminates water dependency entirely. PUE 1.10. Zero municipal water required. Abundant solar for PPA at $35–42/MWh. Cheap land, established fiber, no state income tax. The 'water problem' is irrelevant by design choice.",
    metrics: [
      { label: "Water consumed", value: "Zero" },
      { label: "Solar PPA rate", value: "$38/MWh" },
      { label: "Land cost vs. VA", value: "–80%" },
    ],
  },
  {
    id: "ohio",
    label: "Rural Ohio",
    sub: "No substation",
    rejection: "Rejected — no grid interconnection within 25 miles. Power timeline exceeds 36 months.",
    solution: "Retiring 400MW coal plant 8 miles away — 345kV interconnection already built, utility seeking anchor tenant. Behind-the-meter gas bridge operational in 14 months while interconnection transfer completes. First MW in 14 months vs. 36+ months for a greenfield grid connection.",
    metrics: [
      { label: "Stranded interconnection", value: "345kV / 400MW" },
      { label: "Time to first MW", value: "14 months" },
      { label: "vs. greenfield grid", value: "–22 months" },
    ],
  },
];

const alertTypes = [
  { color: "bg-amber-500", label: "STRANDED_ASSET_DETECTED", example: "AES Cayuga 306MW coal plant retirement moved to Q2 2027. 138kV interconnection. 3 parcels within 4 miles." },
  { color: "bg-emerald-500", label: "QUEUE_WITHDRAWAL", example: "180MW solar withdrew from PJM queue at Possum Point. 180MW freed. Your Stafford County parcel is 12 miles away." },
  { color: "bg-sky-500", label: "GENERATOR_AVAILABLE", example: "2x GE LM6000PF+ listed. 47MW each, low hours. Matches your Midland TX site requirement." },
  { color: "bg-violet-500", label: "UTILITY_EXPANSION", example: "Oncor filed for 345kV substation in Ector County. Your Odessa parcel moves to 400MW available in 30 months." },
  { color: "bg-red-500", label: "MORATORIUM_RISK", example: "Loudoun County Board agenda: data center moratorium discussion June 15. You have 2 parcels in LOI stage — ACCELERATE." },
  { color: "bg-blue-500", label: "DROUGHT_ESCALATION", example: "Maricopa County moved to D3 Extreme. Municipal curtailment probability 40%. Accelerate liquid cooling conversion." },
];

const portfolioParcels = [
  { x: "18%", y: "35%", color: "bg-emerald-400", status: "Qualified" },
  { x: "22%", y: "55%", color: "bg-emerald-400", status: "Qualified" },
  { x: "28%", y: "42%", color: "bg-amber-400", status: "In Progress" },
  { x: "35%", y: "38%", color: "bg-emerald-400", status: "Qualified" },
  { x: "38%", y: "60%", color: "bg-blue-400", status: "New" },
  { x: "42%", y: "48%", color: "bg-amber-400", status: "In Progress" },
  { x: "45%", y: "32%", color: "bg-emerald-400", status: "Qualified" },
  { x: "48%", y: "55%", color: "bg-blue-400", status: "New" },
  { x: "52%", y: "44%", color: "bg-amber-400", status: "In Progress" },
  { x: "55%", y: "38%", color: "bg-emerald-400", status: "Qualified" },
  { x: "58%", y: "62%", color: "bg-blue-400", status: "New" },
  { x: "62%", y: "40%", color: "bg-emerald-400", status: "Qualified" },
  { x: "65%", y: "52%", color: "bg-amber-400", status: "In Progress" },
  { x: "70%", y: "36%", color: "bg-emerald-400", status: "Qualified" },
  { x: "72%", y: "56%", color: "bg-blue-400", status: "New" },
  { x: "76%", y: "44%", color: "bg-emerald-400", status: "Qualified" },
  { x: "80%", y: "38%", color: "bg-amber-400", status: "In Progress" },
  { x: "84%", y: "50%", color: "bg-blue-400", status: "New" },
];

const portfolioTopParcels = [
  { id: "TX-Ector-2241", state: "TX", mw: "340 MW", months: "11 mo" },
  { id: "TN-Rutherford-0891", state: "TN", mw: "200 MW", months: "13 mo" },
  { id: "AZ-Pinal-3304", state: "AZ", mw: "150 MW", months: "12 mo" },
];

const matrixRows = [
  { pathway: "Grid", mw: "80 MW", timeline: "28 months", capex: "$225K/MW", energy: "$65/MWh", rank: "4", highlight: false },
  { pathway: "Behind-meter", mw: "200 MW", timeline: "16 months", capex: "$1.6M/MW", energy: "$45/MWh", rank: "2", highlight: false },
  { pathway: "PPA Match", mw: "150 MW", timeline: "12 months", capex: "—", energy: "$42/MWh", rank: "3", highlight: false },
  { pathway: "SMR", mw: "300 MW", timeline: "72 months", capex: "$8M/MW", energy: "$90/MWh", rank: "5", highlight: false },
  { pathway: "Rapid Deploy", mw: "200 MW", timeline: "10 months", capex: "$2.1M/MW", energy: "$55/MWh", rank: "1", highlight: true },
];

const stakeholders = [
  { title: "Development Team", icon: Factory, blurb: "Your team sees the pipeline: parcels ranked by speed-to-MW, power and water solutions pre-modeled, deal economics assembled. Decision in hours, not quarters.", policies: ["Power Strategy", "Water Strategy", "Deal Economics"], value: "Every parcel arrives with a fully-costed phased deployment plan. The team evaluates solutions, not raw land." },
  { title: "Engineering Partner", icon: Briefcase, blurb: "Your partner — the one who built Switch, Aligned, and Colossus — sees the Power Solution Matrix and says 'I can build that' or 'swap those turbines.' His expertise amplifies the AI, not the other way around.", policies: ["Behind-the-Meter", "Rapid Deploy", "Liquid Cooling"], value: "The AI surfaces opportunities. The builder decides how to execute. Speed multiplied by experience." },
  { title: "Capital Partners", icon: Landmark, blurb: "BlackRock, KKR, Blackstone — they buy finished campuses. Show them a portfolio of 20 sites with phased power strategies, community benefit packages, and revenue projections. They write the check tomorrow.", policies: ["Portfolio Risk", "Revenue Acceleration", "Exit Timing"], value: "Governed intelligence over an asset class. Not a pitch deck — an operating system for infrastructure capital." },
  { title: "Hyperscaler Tenants", icon: Server, blurb: "Microsoft, Google, Meta, CoreWeave — they need megawatts yesterday. Show them a site that's 10 months to first MW with behind-the-meter gas and a PPA overlay. They're signing before the grid interconnection would have even started.", policies: ["Speed-to-MW", "Renewable Mix", "Expansion Path"], value: "The tenant cares about when they get power, not how you solved the problem. Speed is the entire pitch." },
  { title: "Utilities + Municipalities", icon: Building2, blurb: "You're not asking for power — you're solving their problem. Anchor tenant for a planned substation. Revenue for a struggling water utility. Free heat for the school district. Walk in as a partner.", policies: ["Community Benefit", "Utility Partnership", "Heat Monetization"], value: "The AI generates the deal structure that makes the utility say yes. Load growth they need, revenue they want, community goodwill they crave." },
  { title: "Permitting + Government", icon: Globe, blurb: "County commissioners see jobs, tax revenue, and free heat to every school in the district. The AI auto-generates community benefit packages from waste heat analysis. Rezoning approval probability jumps from 55% to 92%.", policies: ["Entitlement Strategy", "Community Benefit", "Environmental"], value: "Transform opposition into invitation. The AI models what the community needs and shows how your project delivers it." },
];

const progression = ["Ingest", "Power", "Water", "Economics", "Pipeline"];

// ─────────────────────────────────────────────────────────────
// SUB-COMPONENTS
// ─────────────────────────────────────────────────────────────

function SectionLabel({ index, children }) {
  return (
    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-400 mb-2">
      {String(index).padStart(2, "0")} / {children}
    </p>
  );
}

function BadgePill({ label, status }) {
  const colors = {
    red: "border-red-300 bg-red-50 text-red-700",
    amber: "border-amber-300 bg-amber-50 text-amber-700",
    green: "border-emerald-300 bg-emerald-50 text-emerald-700",
  };
  const dots = {
    red: "bg-red-500",
    amber: "bg-amber-500",
    green: "bg-emerald-500",
  };
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${colors[status]}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${dots[status]}`} />
      {label}
    </span>
  );
}

function SiteRescueWalkthrough() {
  const [step, setStep] = useState(0);
  const [auto, setAuto] = useState(false);

  useEffect(() => {
    if (!auto) return;
    if (step >= rescueSteps.length - 1) { setAuto(false); return; }
    const t = setTimeout(() => setStep((s) => s + 1), 2200);
    return () => clearTimeout(t);
  }, [auto, step]);

  const current = rescueSteps[step];
  const isFinal = step === rescueSteps.length - 1;

  return (
    <div className="grid gap-6 lg:grid-cols-[0.85fr_1.15fr] lg:items-start">
      {/* Step rail */}
      <div className="space-y-2">
        {rescueSteps.map((s, i) => {
          const done = i < step;
          const active = i === step;
          return (
            <div key={s.id}>
              <button
                onClick={() => { setStep(i); setAuto(false); }}
                className={`w-full rounded-[24px] border p-4 text-left transition-all duration-200 ${
                  active
                    ? "border-zinc-900 bg-zinc-900 text-white shadow-xl dark:border-white dark:bg-white dark:text-zinc-900"
                    : done
                    ? "border-emerald-200 bg-emerald-50 dark:border-emerald-900 dark:bg-emerald-950/30"
                    : "border-zinc-200 bg-white/80 hover:border-zinc-300 dark:border-zinc-800 dark:bg-zinc-900/70"
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                    active ? "bg-white text-zinc-900 dark:bg-zinc-900 dark:text-white"
                    : done ? "bg-emerald-500 text-white"
                    : "bg-zinc-200 text-zinc-600 dark:bg-zinc-700 dark:text-zinc-400"
                  }`}>
                    {done ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
                  </div>
                  <div>
                    <div className={`text-sm font-semibold ${active ? "" : done ? "text-emerald-700 dark:text-emerald-400" : "text-zinc-700 dark:text-zinc-300"}`}>
                      {s.label}
                    </div>
                    {s.agent && (
                      <div className={`mt-0.5 text-xs ${active ? "text-zinc-400 dark:text-zinc-600" : "text-zinc-400"}`}>
                        {s.agent}
                      </div>
                    )}
                  </div>
                </div>
              </button>
              {i < rescueSteps.length - 1 && (
                <div className={`ml-[22px] h-2 w-px ${done ? "bg-emerald-300" : "bg-zinc-200 dark:bg-zinc-700"}`} />
              )}
            </div>
          );
        })}
        <div className="flex gap-2 pt-2">
          <Button
            size="sm"
            className="rounded-xl"
            onClick={() => { setStep(0); setAuto(true); }}
            variant="default"
          >
            ▶ Auto-play
          </Button>
          <Button
            size="sm"
            className="rounded-xl"
            onClick={() => { if (step < rescueSteps.length - 1) setStep((s) => s + 1); }}
            variant="outline"
            disabled={step === rescueSteps.length - 1}
          >
            Next →
          </Button>
        </div>
      </div>

      {/* Detail panel */}
      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.26 }}
        >
          <Card className={`rounded-[32px] border-2 shadow-xl transition-all duration-500 ${
            isFinal
              ? "border-emerald-400 bg-gradient-to-br from-emerald-950 to-zinc-900 dark:border-emerald-400"
              : "border-zinc-100 bg-gradient-to-br from-white to-zinc-50 dark:border-zinc-800 dark:from-zinc-900 dark:to-zinc-950"
          }`}>
            <CardContent className="p-8">
              {/* Parcel header */}
              <div className="mb-5 flex items-center gap-3">
                <div className="rounded-2xl bg-zinc-900 p-2.5 text-white dark:bg-zinc-700">
                  <MapPin className="h-4 w-4" />
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-400">Parcel</div>
                  <div className="font-bold text-zinc-900 dark:text-zinc-100">AZ-Maricopa-4471 · 847 ac · Maricopa County, AZ</div>
                </div>
              </div>

              {/* Status badges */}
              <div className="mb-5 flex flex-wrap gap-2">
                {current.badges.map((b) => (
                  <motion.div
                    key={b.label}
                    initial={{ scale: 0.92 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.2 }}
                  >
                    <BadgePill label={b.label} status={b.status} />
                  </motion.div>
                ))}
              </div>

              <p className={`mb-4 text-sm leading-7 ${isFinal ? "text-emerald-200" : "text-zinc-600 dark:text-zinc-300"}`}>
                {current.description}
              </p>

              {current.detail && (
                <div className={`rounded-2xl border p-4 text-sm leading-7 ${
                  isFinal
                    ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-200"
                    : "border-zinc-200 bg-zinc-50 text-zinc-700 dark:border-zinc-700 dark:bg-zinc-800/50 dark:text-zinc-300"
                }`}>
                  {current.detail}
                </div>
              )}

              {isFinal && current.result && (
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 }}
                  className="mt-5 grid gap-3 sm:grid-cols-3"
                >
                  {[
                    { label: "Speed", value: current.result.speed },
                    { label: "Cost advantage", value: current.result.cost },
                    { label: "Revenue acceleration", value: current.result.acceleration },
                  ].map((m) => (
                    <div key={m.label} className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-4 text-center">
                      <div className="text-xs font-semibold uppercase tracking-wider text-emerald-400">{m.label}</div>
                      <div className="mt-1 text-sm font-bold text-emerald-300">{m.value}</div>
                    </div>
                  ))}
                </motion.div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

function ScenarioToggle() {
  const [active, setActive] = useState(scenarios[0].id);
  const [showAll, setShowAll] = useState(false);
  const current = scenarios.find((s) => s.id === active) || scenarios[0];

  return (
    <div>
      {/* Scenario buttons */}
      <div className="mb-6 flex flex-wrap gap-3">
        {scenarios.map((s) => (
          <button
            key={s.id}
            onClick={() => setActive(s.id)}
            className={`rounded-2xl border px-5 py-2.5 text-left transition-all ${
              active === s.id
                ? "border-zinc-900 bg-zinc-900 text-white shadow-lg dark:border-white dark:bg-white dark:text-zinc-900"
                : "border-zinc-200 bg-white hover:border-zinc-300 dark:border-zinc-700 dark:bg-zinc-900"
            }`}
          >
            <div className="text-sm font-semibold">{s.label}</div>
            <div className="text-xs text-zinc-400">{s.sub}</div>
          </button>
        ))}
      </div>

      {/* Scenario panel */}
      <AnimatePresence mode="wait">
        <motion.div
          key={active}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.24 }}
          className="grid gap-4 lg:grid-cols-2"
        >
          {/* Left: rejection */}
          <Card className="rounded-[28px] border border-red-100 bg-red-50/60 shadow-sm dark:border-red-950 dark:bg-red-950/20">
            <CardContent className="p-6">
              <div className="mb-3 flex items-center gap-2">
                <div className="h-2.5 w-2.5 rounded-full bg-red-500" />
                <span className="text-xs font-bold uppercase tracking-[0.15em] text-red-600 dark:text-red-400">
                  Traditional approach
                </span>
              </div>
              <p className="text-sm leading-7 text-red-800 dark:text-red-300">
                {current.rejection}
              </p>
            </CardContent>
          </Card>

          {/* Right: solution */}
          <Card className="rounded-[28px] border border-emerald-200 bg-emerald-50/60 shadow-sm dark:border-emerald-900 dark:bg-emerald-950/20">
            <CardContent className="p-6">
              <div className="mb-3 flex items-center gap-2">
                <div className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
                <span className="text-xs font-bold uppercase tracking-[0.15em] text-emerald-700 dark:text-emerald-400">
                  Land creation engine
                </span>
              </div>
              <p className="mb-4 text-sm leading-7 text-emerald-900 dark:text-emerald-200">
                {current.solution}
              </p>
              <div className="flex flex-wrap gap-2">
                {current.metrics.map((m) => (
                  <div key={m.label} className="rounded-xl border border-emerald-300 bg-white px-3 py-1.5 dark:border-emerald-800 dark:bg-zinc-900">
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-emerald-600 dark:text-emerald-400">{m.label}</div>
                    <div className="text-sm font-bold text-emerald-800 dark:text-emerald-300">{m.value}</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </AnimatePresence>

      {/* Show all toggle */}
      <div className="mt-4">
        <button
          onClick={() => setShowAll((v) => !v)}
          className="text-xs font-semibold text-zinc-400 underline underline-offset-2 hover:text-zinc-600 dark:hover:text-zinc-300"
        >
          {showAll ? "Hide full comparison table" : "View all comparisons"}
        </button>
        <AnimatePresence>
          {showAll && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="mt-4 overflow-hidden"
            >
              <div className="overflow-x-auto rounded-[24px] border bg-white shadow-sm dark:bg-zinc-900/70">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-zinc-50 dark:bg-zinc-800/50">
                      <th className="w-[160px] px-6 py-4 text-left font-semibold text-zinc-500">Scenario</th>
                      <th className="px-6 py-4 text-left font-semibold text-zinc-600 dark:text-zinc-300">Traditional approach</th>
                      <th className="px-6 py-4 text-left font-semibold text-emerald-700 dark:text-emerald-400">Land creation engine</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scenarios.map((s, i) => (
                      <tr key={s.id} className="border-b last:border-0">
                        <td className="px-6 py-4 font-semibold text-zinc-700 dark:text-zinc-300">
                          <div>{s.label}</div>
                          <div className="text-xs font-normal text-zinc-400">{s.sub}</div>
                        </td>
                        <td className="px-6 py-4 text-zinc-500 dark:text-zinc-400">{s.rejection}</td>
                        <td className="px-6 py-4 font-medium text-zinc-900 dark:text-zinc-100">{s.solution}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

function PortfolioDashboard() {
  return (
    <Card className="rounded-[32px] border-0 bg-zinc-900 text-white shadow-2xl">
      <CardContent className="p-8 lg:p-10">
        <div className="mb-6">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">Live Portfolio</p>
          <h3 className="mt-1 text-xl font-bold">Active Parcel Dashboard</h3>
        </div>

        <div className="grid gap-8 lg:grid-cols-[1.4fr_0.6fr]">
          {/* Map */}
          <div className="relative h-56 overflow-hidden rounded-2xl border border-white/10 bg-zinc-800/60">
            {/* Simplified US outline placeholder */}
            <svg viewBox="0 0 400 250" className="absolute inset-0 h-full w-full opacity-20" fill="none">
              <path d="M60,80 L80,60 L120,55 L160,50 L200,48 L240,50 L280,55 L320,60 L350,80 L360,110 L355,140 L340,160 L320,175 L280,185 L240,190 L200,192 L160,190 L120,185 L90,175 L70,160 L55,140 L50,110 Z" stroke="white" strokeWidth="1.5" />
              <path d="M60,80 L55,110 L50,140 L70,160" stroke="white" strokeWidth="1.5" />
            </svg>
            {/* Parcel dots */}
            {portfolioParcels.map((p, i) => (
              <motion.div
                key={i}
                initial={{ scale: 0, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.3, delay: i * 0.04 }}
                viewport={{ once: true }}
                className={`absolute h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full shadow-lg ${p.color}`}
                style={{ left: p.x, top: p.y }}
                title={p.status}
              />
            ))}
            {/* Legend */}
            <div className="absolute bottom-3 left-3 flex flex-col gap-1">
              {[
                { color: "bg-emerald-400", label: "Qualified" },
                { color: "bg-amber-400", label: "In Progress" },
                { color: "bg-blue-400", label: "New" },
              ].map((l) => (
                <div key={l.label} className="flex items-center gap-1.5">
                  <div className={`h-2 w-2 rounded-full ${l.color}`} />
                  <span className="text-[10px] text-zinc-400">{l.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-1">
            {[
              { value: "47", label: "Active parcels" },
              { value: "12", label: "Solutions this week" },
              { value: "3", label: "Stranded assets" },
              { value: "13.2 mo", label: "Avg speed-to-MW" },
              { value: "4.2 GW", label: "Portfolio capacity" },
            ].map((s) => (
              <div key={s.label} className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
                <div className="text-xl font-bold text-white">{s.value}</div>
                <div className="text-xs text-zinc-500">{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Top parcels table */}
        <div className="mt-6 rounded-2xl border border-white/10 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 bg-white/5">
                <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-zinc-500">Parcel</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-zinc-500">State</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-zinc-500">MW</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-zinc-500">Speed rank</th>
              </tr>
            </thead>
            <tbody className="text-zinc-300">
              {portfolioTopParcels.map((p, i) => (
                <tr key={p.id} className="border-b border-white/5 last:border-0">
                  <td className="px-4 py-2.5 font-mono text-xs">{p.id}</td>
                  <td className="px-4 py-2.5">{p.state}</td>
                  <td className="px-4 py-2.5">{p.mw}</td>
                  <td className="px-4 py-2.5">
                    <span className="rounded-lg bg-emerald-500/20 px-2 py-0.5 text-xs font-bold text-emerald-400">#{i + 1} · {p.months}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function MondayBriefing() {
  return (
    <Card className="rounded-[32px] border border-zinc-200 bg-white shadow-lg dark:border-zinc-700 dark:bg-zinc-900">
      <CardContent className="p-8 lg:p-10">
        {/* Header — monospace report feel */}
        <div className="mb-6 border-b border-zinc-200 pb-5 dark:border-zinc-700">
          <div className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">
            Weekly Land Intelligence Briefing
          </div>
          <div className="font-mono text-xs text-zinc-400">Week of June 9, 2025 · Generated 06:00 AM</div>
          <h3 className="mt-2 text-xl font-bold text-zinc-900 dark:text-zinc-100">
            3 new opportunities · 2 alerts requiring action
          </h3>
        </div>

        {/* Opportunities */}
        <div className="mb-6">
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500">
            New opportunities ranked by speed-to-MW
          </p>
          <div className="space-y-3">
            {[
              { rank: 1, location: "Ector County, TX", detail: "340MW gas interconnection from retiring peaker plant. 11 months to first MW. Acquisition window closes Aug 15.", urgency: "HIGH", color: "text-red-600 bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-900 dark:text-red-400" },
              { rank: 2, location: "Rutherford County, TN", detail: "200MW behind-meter gas + 8 thermal consumers for heat monetization. Community benefit package auto-generated. Rezoning probability: 88%.", urgency: "MODERATE", color: "text-amber-700 bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-900 dark:text-amber-400" },
              { rank: 3, location: "Pinal County, AZ", detail: "150MW PPA match with stalled solar+storage project seeking anchor offtaker. Developer responsive. COD Q1 2027.", urgency: "MODERATE", color: "text-amber-700 bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-900 dark:text-amber-400" },
            ].map((opp) => (
              <div key={opp.rank} className="flex gap-4 rounded-2xl border border-zinc-100 bg-zinc-50 p-4 dark:border-zinc-800 dark:bg-zinc-800/40">
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-zinc-900 text-xs font-bold text-white dark:bg-zinc-100 dark:text-zinc-900">
                  {opp.rank}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-zinc-900 dark:text-zinc-100">{opp.location}</span>
                    <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${opp.color}`}>
                      {opp.urgency}
                    </span>
                  </div>
                  <p className="mt-1 text-sm leading-6 text-zinc-500 dark:text-zinc-400">{opp.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Alerts */}
        <div className="mb-6">
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500">
            Alerts requiring immediate action
          </p>
          <div className="space-y-2">
            {[
              { dot: "bg-red-500", text: "MORATORIUM RISK: Prince William County, VA — board agenda item June 12. 2 parcels in LOI stage. Recommend: accelerate or withdraw." },
              { dot: "bg-emerald-500", text: "GENERATOR AVAILABLE: 2× GE LM6000PF+ from cancelled project. 47MW each, low hours. Matches Midland TX site requirement. Broker: Waukesha-Pearce." },
            ].map((a, i) => (
              <div key={i} className="flex items-start gap-3 rounded-xl border border-zinc-100 p-3 dark:border-zinc-800">
                <div className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${a.dot}`} />
                <p className="text-sm leading-6 text-zinc-700 dark:text-zinc-300">{a.text}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Portfolio summary footer */}
        <div className="rounded-2xl border border-zinc-200 bg-zinc-50 px-5 py-3 dark:border-zinc-700 dark:bg-zinc-800/50">
          <p className="font-mono text-xs text-zinc-500">
            Portfolio summary: <span className="font-bold text-zinc-700 dark:text-zinc-300">47 active parcels</span> ·{" "}
            <span className="font-bold text-zinc-700 dark:text-zinc-300">4.2 GW total capacity</span> ·{" "}
            <span className="font-bold text-zinc-700 dark:text-zinc-300">Avg speed-to-MW: 13.2 months</span>
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

function StageButton({ stage, active, onClick, showConnector }) {
  const { Icon } = stage;
  const isActive = active.id === stage.id;
  return (
    <>
      <button
        onClick={() => onClick(stage)}
        className={`w-full rounded-[28px] border p-5 text-left transition-all duration-200 ${
          isActive
            ? "border-zinc-900 bg-zinc-900 text-white shadow-2xl dark:border-white dark:bg-white dark:text-zinc-900"
            : "border-zinc-200 bg-white/80 hover:border-zinc-300 hover:bg-white dark:border-zinc-800 dark:bg-zinc-900/70 dark:hover:border-zinc-600"
        }`}
      >
        <div className="flex items-start gap-4">
          <div className={`shrink-0 rounded-2xl bg-gradient-to-br ${stage.color} p-3 text-white shadow-lg`}>
            <Icon className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className={`text-[10px] font-semibold uppercase tracking-[0.18em] ${isActive ? "opacity-50" : "text-zinc-400"}`}>
                  {stage.num}
                </div>
                <div className="mt-0.5 text-[17px] font-semibold leading-tight">{stage.label}</div>
              </div>
              <ArrowRight className={`h-4 w-4 shrink-0 transition-transform duration-200 ${isActive ? "translate-x-1 opacity-60" : "opacity-30"}`} />
            </div>
            <p className={`mt-1.5 text-sm leading-relaxed ${isActive ? "text-zinc-300 dark:text-zinc-600" : "text-zinc-500"}`}>
              {stage.sub}
            </p>
          </div>
        </div>
      </button>
      {showConnector && <div className="ml-[30px] h-5 w-px bg-zinc-200 dark:bg-zinc-700" />}
    </>
  );
}

function DetailPanel({ stage }) {
  const { Icon } = stage;
  return (
    <AnimatePresence mode="wait">
      <motion.div key={stage.id} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.26 }} className="h-full">
        <Card className="h-full rounded-[32px] border-0 bg-gradient-to-br from-white to-zinc-50 shadow-xl dark:from-zinc-900 dark:to-zinc-950">
          <CardContent className="p-8 lg:p-10">
            <div className="mb-6 flex items-center gap-4">
              <div className={`rounded-3xl bg-gradient-to-br ${stage.color} p-4 text-white shadow-xl`}>
                <Icon className="h-8 w-8" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-400">Stage {stage.num}</p>
                <h3 className="mt-1 text-2xl font-bold tracking-tight text-zinc-900 dark:text-zinc-100">{stage.label}</h3>
              </div>
            </div>
            <p className="mb-8 text-sm leading-7 text-zinc-600 dark:text-zinc-300">{stage.summary}</p>
            <div className="grid gap-3 sm:grid-cols-2">
              {stage.bullets.map((item) => (
                <div key={item} className={`flex items-start gap-3 rounded-2xl border ${stage.lightBorder} ${stage.lightBg} p-4 dark:border-zinc-800 dark:bg-zinc-900/50`}>
                  <div className={`mt-1 h-2 w-2 shrink-0 rounded-full ${stage.dotColor}`} />
                  <p className="text-sm leading-relaxed text-zinc-700 dark:text-zinc-300">{item}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </AnimatePresence>
  );
}

function StoryView({ activeStage, setActiveStage }) {
  return (
    <div className="grid gap-6 lg:grid-cols-[0.85fr_1.15fr] lg:items-start">
      <div>
        {stages.map((stage, idx) => (
          <StageButton key={stage.id} stage={stage} active={activeStage} onClick={setActiveStage} showConnector={idx < stages.length - 1} />
        ))}
      </div>
      <DetailPanel stage={activeStage} />
    </div>
  );
}

function StakeholderView() {
  const [selected, setSelected] = useState(stakeholders[0].title);
  const current = stakeholders.find((s) => s.title === selected) || stakeholders[0];
  const Icon = current.icon;
  return (
    <div className="grid gap-6 lg:grid-cols-[0.78fr_1.22fr] lg:items-start">
      <Card className="rounded-[32px] border-0 bg-zinc-900 text-white shadow-2xl">
        <CardContent className="p-6 lg:p-8">
          <div className="mb-4 text-sm font-medium uppercase tracking-[0.2em] text-zinc-400">Stakeholders</div>
          <div className="space-y-3">
            {stakeholders.map((item) => {
              const AI = item.icon;
              const active = item.title === selected;
              return (
                <button key={item.title} onClick={() => setSelected(item.title)} className={`flex w-full items-start gap-3 rounded-2xl border px-4 py-4 text-left transition-all ${active ? "border-white/20 bg-white/10" : "border-white/10 bg-white/5 hover:bg-white/[0.08]"}`}>
                  <div className="rounded-2xl bg-white/10 p-2.5"><AI className="h-5 w-5" /></div>
                  <div>
                    <div className="font-semibold">{item.title}</div>
                    <div className="mt-1 text-sm leading-6 text-zinc-300">{item.blurb}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <AnimatePresence mode="wait">
        <motion.div
          key={current.title}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.25 }}
          className="grid gap-6"
        >
          <Card className="rounded-[32px] border-0 bg-gradient-to-br from-white to-zinc-100 shadow-2xl dark:from-zinc-900 dark:to-zinc-950">
            <CardContent className="p-8 lg:p-10">
              <div className="flex items-center gap-4">
                <div className="rounded-3xl bg-zinc-900 p-4 text-white shadow-xl dark:bg-white dark:text-zinc-900">
                  <Icon className="h-8 w-8" />
                </div>
                <div>
                  <div className="text-sm uppercase tracking-[0.2em] text-zinc-500">Stakeholder view</div>
                  <h3 className="mt-1 text-3xl font-semibold tracking-tight">{current.title}</h3>
                </div>
              </div>

              <p className="mt-6 max-w-3xl text-base leading-7 text-zinc-600 dark:text-zinc-300">
                {current.value}
              </p>

              <div className="mt-8 grid gap-4 lg:grid-cols-[0.9fr_1.1fr]">
                <div className="rounded-3xl border bg-white/80 p-5 shadow-sm dark:bg-zinc-900/70">
                  <div className="text-sm font-medium uppercase tracking-[0.2em] text-zinc-500">Policy domains</div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {current.policies.map((policy) => (
                      <Badge key={policy} className="rounded-full px-3 py-1 text-sm">{policy}</Badge>
                    ))}
                  </div>
                </div>
                <div className="rounded-3xl border bg-white/80 p-5 shadow-sm dark:bg-zinc-900/70">
                  <div className="text-sm font-medium uppercase tracking-[0.2em] text-zinc-500">Why this view matters</div>
                  <p className="mt-4 text-sm leading-7 text-zinc-600 dark:text-zinc-300">
                    Each stakeholder sees the same parcel data through a different lens. The developer sees speed-to-MW. The capital partner sees portfolio risk. The municipality sees community benefit. Same corpus, governed by policy.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {stakeholders.map((item, i) => {
              const SmallIcon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25, delay: i * 0.04 }}
                  viewport={{ once: true }}
                >
                  <Card className={`h-full rounded-[28px] border shadow-sm ${item.title === current.title ? "bg-zinc-900 text-white dark:bg-white dark:text-zinc-900" : "bg-white/80 dark:bg-zinc-900/70"}`}>
                    <CardContent className="p-5">
                      <div className={`mb-4 flex h-12 w-12 items-center justify-center rounded-2xl ${item.title === current.title ? "bg-white/10 dark:bg-zinc-200" : "bg-zinc-100 dark:bg-zinc-800"}`}>
                        <SmallIcon className="h-5 w-5" />
                      </div>
                      <h3 className="text-lg font-semibold">{item.title}</h3>
                      <p className={`mt-2 text-sm leading-6 ${item.title === current.title ? "text-zinc-200 dark:text-zinc-700" : "text-zinc-600 dark:text-zinc-300"}`}>
                        {item.blurb}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

function PathwayGrid({ pathways, title, color }) {
  return (
    <div>
      <h4 className="mb-4 text-sm font-semibold uppercase tracking-[0.18em] text-zinc-500">{title}</h4>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {pathways.map((pw, i) => {
          const { Icon } = pw;
          return (
            <motion.div key={pw.label} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay: i * 0.04 }} viewport={{ once: true }}>
              <Card className="h-full rounded-[24px] border border-zinc-100 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-900/70">
                <CardContent className="p-5">
                  <div className={`mb-3 inline-flex rounded-2xl bg-gradient-to-br ${color} p-2.5 text-white shadow-md`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <h5 className="mb-2 text-sm font-bold text-zinc-900 dark:text-zinc-100">{pw.label}</h5>
                  <p className="text-xs leading-relaxed text-zinc-500 dark:text-zinc-400">{pw.detail}</p>
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// MAIN EXPORT
// ─────────────────────────────────────────────────────────────

export default function LandManagementStory() {
  const [activeStage, setActiveStage] = useState(stages[0]);
  const [view, setView] = useState("story");

  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-50 via-white to-zinc-100 text-zinc-900 dark:from-zinc-950 dark:via-zinc-950 dark:to-zinc-900 dark:text-zinc-100">
      <div className="mx-auto max-w-7xl px-6 pb-24 lg:px-10">

        {/* ── #1 PRE-HERO HOOK ─────────────────────────────────── */}
        <section className="pt-10 lg:pt-16">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <Card className="rounded-[24px] border-0 bg-zinc-900 text-white shadow-2xl">
              <CardContent className="px-8 py-6 lg:px-10">
                <p className="text-lg leading-8 text-zinc-200 italic">
                  "We found two generators from a cancelled LNG project. We matched them to a site nobody else was looking at. That deal closed for $2B."
                </p>
                <p className="mt-3 text-sm font-semibold text-zinc-500">
                  This system does that scan every single day.
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </section>

        {/* ── #2 HERO ──────────────────────────────────────────── */}
        <section className="py-14 lg:py-20">
          <div className="grid gap-10 lg:grid-cols-[1.2fr_0.8fr] lg:items-end">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <Badge className="mb-6 rounded-full px-4 py-1.5 text-xs font-semibold tracking-wide">
                From land sifting to land creation
              </Badge>
              <h1 className="text-5xl font-bold leading-[1.1] tracking-tight sm:text-6xl lg:text-7xl">
                <span className="whitespace-nowrap">Don't find</span>{" "}
                <span className="text-zinc-400">qualified sites.</span>
                <br />
                <span className="whitespace-nowrap">Qualify more</span> sites.
              </h1>
              <p className="mt-6 max-w-xl text-lg leading-8 text-zinc-500 dark:text-zinc-400">
                AI-powered land creation engine for data center development. Take sites that are 60% qualified and manufacture the missing 40% — power, water, fiber, hardening, entitlements — faster than anyone else in the market.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                {progression.map((step, i) => (
                  <div key={step} className="flex items-center gap-3">
                    <div className="rounded-full border bg-white px-4 py-2 text-sm font-medium shadow-sm dark:bg-zinc-900">{step}</div>
                    {i < progression.length - 1 && <ArrowRight className="h-4 w-4 text-zinc-400" />}
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.15 }}>
              <Card className="rounded-[28px] border-0 bg-zinc-900 text-white shadow-2xl">
                <CardContent className="p-8">
                  <p className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-zinc-400">Core thesis</p>
                  <p className="mb-4 text-sm leading-7 text-zinc-300">
                    Every site has deficiencies. The question isn't "does it pass?" — it's "what would it take to make it pass, and can we do that faster than anyone else?"
                  </p>
                  <p className="mb-4 text-sm leading-7 text-zinc-300">
                    Land is less than 1% of the deal. Speed is everything. Buy 10, use 6. The 4 you don't use cost less than one quarter of delay.
                  </p>
                  <p className="text-sm leading-7 text-zinc-300">
                    The AI doesn't add friction — it manufactures solutions. Power, water, cooling, permits — there is always a way.
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </section>

        {/* ── #3 CREDIBILITY STRIP ─────────────────────────────── */}
        <section className="border-y border-zinc-200 py-5 dark:border-zinc-800">
          <p className="text-center text-sm font-semibold uppercase tracking-[0.2em] text-zinc-400">
            Built by the team behind the development of AT&T, eBay, Switch, and Aligned Data centers.
          </p>
        </section>

        {/* ── #4 SPEED KILLS CALLOUT ───────────────────────────── */}
        <section className="py-12 lg:py-16">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
            viewport={{ once: true }}
          >
            <Card className="rounded-[32px] border-0 bg-gradient-to-r from-zinc-900 to-zinc-800 text-white shadow-2xl">
              <CardContent className="p-10 lg:p-14 text-center">
                <p className="text-xl font-semibold leading-9 text-zinc-300 sm:text-2xl">
                  Every month of delay costs <span className="text-white">$40–80M</span> in unrealized revenue on a 200MW facility.
                </p>
                <div className="mx-auto mt-8 grid max-w-3xl gap-6 sm:grid-cols-3">
                  <div>
                    <div className="text-4xl font-bold tracking-tight text-emerald-400 lg:text-5xl">$538M</div>
                    <div className="mt-1 text-sm text-zinc-400">Revenue captured from 14-month acceleration</div>
                  </div>
                  <div>
                    <div className="text-4xl font-bold tracking-tight text-zinc-300 lg:text-5xl">$2M</div>
                    <div className="mt-1 text-sm text-zinc-400">Land cost</div>
                  </div>
                  <div>
                    <div className="text-4xl font-bold tracking-tight text-zinc-300 lg:text-5xl">$22M</div>
                    <div className="mt-1 text-sm text-zinc-400">Bridge power cost</div>
                  </div>
                </div>
                <p className="mt-8 text-lg font-semibold text-emerald-400">
                  The acceleration value exceeds both by 20×.
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </section>

        {/* ── 01 / SYSTEM PROGRESSION ──────────────────────────── */}
        <section className="py-10 lg:py-14">
          <div className="mb-8 grid gap-6 lg:grid-cols-[1fr_auto] lg:items-end">
            <div>
              <SectionLabel index={1}>System Progression</SectionLabel>
              <h2 className="text-3xl font-bold tracking-tight">Five stages. One creation engine.</h2>
              <p className="mt-3 max-w-lg text-base leading-7 text-zinc-500 dark:text-zinc-400">
                Each stage adds capability. The platform core — the LLM, the policy engine, the temporal database — never changes. Only what the agents analyze expands.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button onClick={() => setView("story")} className="rounded-2xl px-5" variant={view === "story" ? "default" : "outline"}>System View</Button>
              <Button onClick={() => setView("stakeholder")} className="rounded-2xl px-5" variant={view === "stakeholder" ? "default" : "outline"}>Stakeholder View</Button>
            </div>
          </div>
          <AnimatePresence mode="wait">
            <motion.div key={view} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.28 }}>
              {view === "story" ? <StoryView activeStage={activeStage} setActiveStage={setActiveStage} /> : <StakeholderView />}
            </motion.div>
          </AnimatePresence>
        </section>

        {/* ── #6 ANIMATED SITE RESCUE ──────────────────────────── */}
        <section className="py-10 lg:py-14">
          <div className="mb-10">
            <SectionLabel index={2}>Site Rescue</SectionLabel>
            <h2 className="text-3xl font-bold tracking-tight">Watch a rejected site become a $500M deal.</h2>
            <p className="mt-3 max-w-lg text-base leading-7 text-zinc-500 dark:text-zinc-400">
              Every competitor rejected this parcel. Three red flags: no power, no water, wrong zoning. The creation engine solves all three and produces a site that's faster and cheaper than the "obvious" alternative.
            </p>
          </div>
          <SiteRescueWalkthrough />
        </section>

        {/* ── 03 / CORE PRINCIPLES ─────────────────────────────── */}
        <section className="py-10 lg:py-14">
          <div className="mb-10">
            <SectionLabel index={3}>Core Principles</SectionLabel>
            <h2 className="text-3xl font-bold tracking-tight">Why this isn't a me-too.</h2>
            <p className="mt-3 max-w-lg text-base leading-7 text-zinc-500 dark:text-zinc-400">
              The moat isn't the data — everyone can get county GIS data. The moat is the solution-generation layer and the compounding institutional memory.
            </p>
          </div>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {corePrinciples.map((pillar, i) => {
              const { Icon } = pillar;
              return (
                <motion.div key={pillar.title} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: i * 0.06 }} viewport={{ once: true }}>
                  <Card className="h-full rounded-[28px] border border-zinc-100 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-900/70">
                    <CardContent className="p-6">
                      <div className={`mb-5 inline-flex rounded-2xl bg-gradient-to-br ${pillar.color} p-3 text-white shadow-lg`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <h3 className="mb-2 text-base font-bold text-zinc-900 dark:text-zinc-100">{pillar.title}</h3>
                      <p className="text-sm leading-7 text-zinc-500 dark:text-zinc-400">{pillar.body}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* ── #8 WHAT WE SEE THAT THEY DON'T ──────────────────── */}
        <section className="py-10 lg:py-14">
          <div className="mb-10">
            <SectionLabel index={4}>Competitive Intelligence</SectionLabel>
            <h2 className="text-3xl font-bold tracking-tight">What we see that they don't.</h2>
            <p className="mt-3 max-w-lg text-base leading-7 text-zinc-500 dark:text-zinc-400">
              Three real-world opportunities that traditional site selection misses entirely. The system catches them automatically.
            </p>
          </div>
          <div className="grid gap-5 lg:grid-cols-3">
            {opportunityCards.map((opp, i) => (
              <motion.div key={opp.title} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: i * 0.08 }} viewport={{ once: true }}>
                <Card className="h-full rounded-[28px] border-0 bg-zinc-900 text-white shadow-xl">
                  <CardContent className="flex h-full flex-col p-6">
                    <div className="mb-3 flex items-center gap-2">
                      <div className={`h-2.5 w-2.5 rounded-full ${opp.dot}`} />
                      <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">{opp.label}</span>
                    </div>
                    <h4 className="mb-3 text-lg font-bold">{opp.title}</h4>
                    <p className="mb-5 flex-1 text-sm leading-7 text-zinc-300">{opp.narrative}</p>
                    <div className="flex gap-3 border-t border-white/10 pt-4">
                      <div className="flex-1 rounded-xl bg-white/5 px-3 py-2">
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Time advantage</div>
                        <div className="mt-0.5 text-sm font-bold text-emerald-400">{opp.timeAdvantage}</div>
                      </div>
                      <div className="flex-1 rounded-xl bg-white/5 px-3 py-2">
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Opportunity value</div>
                        <div className="mt-0.5 text-sm font-bold text-emerald-400">{opp.opportunityValue}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </section>

        {/* ── 05 / AGENT DEEP DIVE ─────────────────────────────── */}
        <section className="py-10 lg:py-14">
          <div className="mb-10">
            <SectionLabel index={5}>Agent Deep Dive</SectionLabel>
            <h2 className="text-3xl font-bold tracking-tight">Six ways to power. Seven ways to cool.</h2>
            <p className="mt-3 max-w-lg text-base leading-7 text-zinc-500 dark:text-zinc-400">
              Every candidate parcel gets all pathways evaluated in parallel. The output isn't a score — it's a fully-costed, phased deployment strategy with a speed comparison against the market alternative.
            </p>
          </div>
          <div className="space-y-10">
            <PathwayGrid pathways={powerPathways} title="⚡ Power Agent — 6 Pathways" color="from-amber-500 to-orange-400" />
            <PathwayGrid pathways={waterPathways} title="💧 Water Agent — 7 Pathways" color="from-blue-500 to-cyan-500" />
          </div>
        </section>

        {/* ── 06 / THE INVERSION — INTERACTIVE SCENARIO TOGGLE ── */}
        <section className="py-10 lg:py-14">
          <div className="mb-10">
            <SectionLabel index={6}>The Inversion</SectionLabel>
            <h2 className="text-3xl font-bold tracking-tight">Pick the hard case. Watch us solve it.</h2>
            <p className="mt-3 max-w-lg text-base leading-7 text-zinc-500 dark:text-zinc-400">
              Every scenario below is a site that competitors reject. Click one and see how the creation engine converts it into a deal.
            </p>
          </div>
          <ScenarioToggle />
        </section>

        {/* ── 07 / 24/7 INTELLIGENCE ───────────────────────────── */}
        <section className="py-10 lg:py-14">
          <div className="mb-10">
            <SectionLabel index={7}>24/7 Intelligence</SectionLabel>
            <h2 className="text-3xl font-bold tracking-tight">The machine that never sleeps.</h2>
            <p className="mt-3 max-w-lg text-base leading-7 text-zinc-500 dark:text-zinc-400">
              Daily scans, weekly briefings, instant alerts. Every retiring power plant, every freed interconnection, every available generator — detected automatically.
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {alertTypes.map((alert, i) => (
              <motion.div key={alert.label} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay: i * 0.05 }} viewport={{ once: true }}>
                <Card className="h-full rounded-[24px] border border-zinc-100 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-900/70">
                  <CardContent className="p-5">
                    <div className="mb-3 flex items-center gap-2">
                      <div className={`h-2.5 w-2.5 rounded-full ${alert.color}`} />
                      <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">{alert.label}</span>
                    </div>
                    <p className="text-sm leading-relaxed text-zinc-700 dark:text-zinc-300">{alert.example}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </section>

        {/* ── 08 / MONDAY MORNING BRIEFING ─────────────────────── */}
        <section className="py-10 lg:py-14">
          <div className="mb-10">
            <SectionLabel index={8}>Monday Morning</SectionLabel>
            <h2 className="text-3xl font-bold tracking-tight">What your team gets every Monday at 6 AM.</h2>
            <p className="mt-3 max-w-lg text-base leading-7 text-zinc-500 dark:text-zinc-400">
              Machine-generated intelligence briefing. Opportunities ranked by speed. Alerts prioritized by urgency. Portfolio status at a glance.
            </p>
          </div>
          <MondayBriefing />
        </section>

        {/* ── 09 / PORTFOLIO DASHBOARD ─────────────────────────── */}
        <section className="py-10 lg:py-14">
          <div className="mb-10">
            <SectionLabel index={9}>Portfolio View</SectionLabel>
            <h2 className="text-3xl font-bold tracking-tight">The operating system for land acquisition.</h2>
            <p className="mt-3 max-w-lg text-base leading-7 text-zinc-500 dark:text-zinc-400">
              Every active parcel, every solution in progress, every deal ranked by speed-to-MW. Not a spreadsheet — a live intelligence layer.
            </p>
          </div>
          <PortfolioDashboard />
        </section>

        {/* ── 10 / DECISION SURFACE — POWER SOLUTION MATRIX ───── */}
        <section className="py-10 lg:py-14">
          <div className="mb-10">
            <SectionLabel index={10}>Decision Surface</SectionLabel>
            <h2 className="text-3xl font-bold tracking-tight">What your team actually sees.</h2>
            <p className="mt-3 max-w-lg text-base leading-7 text-zinc-500 dark:text-zinc-400">
              Not a score. Not a ranking. A Power Solution Matrix with phased deployment strategy and market comparison — the artifact that moves a deal.
            </p>
          </div>

          <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} viewport={{ once: true }}>
            <Card className="rounded-[32px] border-0 bg-zinc-900 text-white shadow-2xl">
              <CardContent className="p-8 lg:p-10">
                <div className="mb-6 flex items-center gap-3">
                  <div className="rounded-2xl bg-gradient-to-br from-amber-500 to-orange-400 p-3 text-white shadow-lg">
                    <Zap className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500">Power Solution Matrix</p>
                    <h3 className="text-xl font-bold">Parcel: AZ-Maricopa-4471</h3>
                  </div>
                </div>

                <div className="overflow-x-auto rounded-2xl border border-white/10">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-white/10 bg-white/5">
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-zinc-400">Pathway</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-zinc-400">MW</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-zinc-400">Timeline</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-zinc-400">Capex / MW</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-zinc-400">Energy $/MWh</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-zinc-400">Rank</th>
                      </tr>
                    </thead>
                    <tbody className="text-zinc-300">
                      {matrixRows.map((row) => (
                        <tr key={row.pathway} className={row.highlight ? "bg-emerald-500/10" : "border-b border-white/5"}>
                          <td className={`px-4 py-3 ${row.highlight ? "font-semibold text-emerald-400" : ""}`}>{row.highlight ? "⭐ " : ""}{row.pathway}</td>
                          <td className={`px-4 py-3 ${row.highlight ? "text-emerald-400" : ""}`}>{row.mw}</td>
                          <td className={`px-4 py-3 ${row.highlight ? "text-emerald-400" : ""}`}>{row.timeline}</td>
                          <td className={`px-4 py-3 ${row.highlight ? "text-emerald-400" : ""}`}>{row.capex}</td>
                          <td className={`px-4 py-3 ${row.highlight ? "text-emerald-400" : ""}`}>{row.energy}</td>
                          <td className={`px-4 py-3 ${row.highlight ? "font-bold text-emerald-400" : "text-zinc-500"}`}>{row.rank}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <p className="mt-3 text-xs text-zinc-600">
                  Illustrative analysis. Actual values generated per-parcel by the Power Agent from live connector data. PPA shows no capex — it is a contracted energy rate, not a capital expenditure.
                </p>

                <div className="mt-8 grid gap-4 sm:grid-cols-2">
                  <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
                    <p className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500">Recommended strategy</p>
                    <div className="space-y-2 text-sm text-zinc-300">
                      <p><span className="font-semibold text-emerald-400">Phase 1:</span> Rapid deploy 4× LM6000 — 10 months to 200MW</p>
                      <p><span className="font-semibold text-amber-400">Phase 2:</span> Behind-meter gas permanent — 16 months</p>
                      <p><span className="font-semibold text-sky-400">Phase 3:</span> PPA from Sonoran Solar II — 12 months, 150MW</p>
                      <p><span className="font-semibold text-zinc-400">Phase 4:</span> Grid interconnection completes — 28 months, 80MW</p>
                      <p><span className="font-semibold text-violet-400">Phase 5:</span> SMR evaluation for 2032+ expansion</p>
                    </div>
                  </div>
                  <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
                    <p className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500">vs. "Perfect" site in Loudoun County</p>
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-zinc-400">Time to first MW</span>
                        <span className="font-bold text-emerald-400">14 months faster</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-zinc-400">Total deal cost</span>
                        <span className="font-bold text-emerald-400">$310M cheaper</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-zinc-400">Revenue acceleration</span>
                        <span className="font-bold text-emerald-400">+$538M captured</span>
                      </div>
                      <div className="mt-2 border-t border-white/10 pt-3">
                        <p className="text-xs text-zinc-500">This "imperfect" site outperforms the obvious market alternative by every metric that matters.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </section>

        {/* ── #15 CLOSING QUESTION ─────────────────────────────── */}
        <section className="pt-8 pb-4">
          <Card className="rounded-[32px] border-0 bg-gradient-to-r from-zinc-900 to-zinc-800 text-white shadow-2xl">
            <CardContent className="p-8 lg:p-10">
              <p className="text-sm font-medium uppercase tracking-[0.2em] text-zinc-400">Closing frame</p>
              <h2 className="mt-2 text-3xl font-semibold tracking-tight">
                How fast could you move if you had 200 qualified sites instead of 5?
              </h2>
              <p className="mt-4 max-w-3xl text-base leading-7 text-zinc-300">
                The AI doesn't replace the builder who did Switch, Aligned, and Colossus. It feeds him 200 opportunities a month instead of 5 — with power solutions pre-modeled, cooling strategies pre-costed, and community benefit packages pre-written.
              </p>
              <p className="mt-4 text-base leading-7 text-zinc-400">
                There is always a way. The machine finds it.
              </p>
            </CardContent>
          </Card>
        </section>

      </div>
    </div>
  );
}